import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Home,
  Dog,
  Cat,
  Users,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Facebook,
  Linkedin,
} from "lucide-react";
import { DogIconOutlined, CatIconOutlined } from "./Icons/CustomIcons";
import { IS_NATIVE_APP } from "../config/appTarget.js";
import { openExternalUrl } from "../lib/openExternalUrl.js";
import { LanguageSwitcher } from "../i18n/LanguageSwitcher.js";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: t("nav.home"), href: "/", icon: <Home className="w-4 h-4" /> },
    { label: t("nav.adopt"), href: "/adotar", icon: <Heart className="w-4 h-4" /> },
    {
      label: t("animal.dogs"),
      href: "/caes?page=1",
      icon: <DogIconOutlined fill="white" strokeWidth={0.9} size={18} />,
    },
    {
      label: t("animal.cats"),
      href: "/gatos?page=1",
      icon: <CatIconOutlined fill="white" strokeWidth={0.9} size={18} />,
    },
    // {
    //   label: "PawMatch",
    //   href: "/pawmatch",
    //   icon: <Heart className="w-4 h-4" />,
    // },
    {
      label: t("nav.favorites"),
      href: "/favoritos",
      icon: <Heart className="w-4 h-4" />,
    },
  ];

  const adoptionLinks = [
    { label: t("nav.ownerTutorial"), href: "/tutorial-dono" },
    { label: t("nav.whyAdopt"), href: "/porque-adotar" },
    { label: t("nav.adoptionProcess"), href: "/tutorial-dono/processo" },
    { label: t("nav.beforeAdopting"), href: "/tutorial-dono/antes" },
    { label: t("nav.afterAdopting"), href: "/tutorial-dono/depois" },
  ];

  const aboutLinks = [
    { label: t("nav.aboutPawseum"), href: "/sobre-pawseum" },
    { label: t("nav.blog"), href: "/blog" },
    { label: t("nav.ourMission"), href: "/sobre-pawseum#mission" },
    { label: t("nav.founders"), href: "/sobre-pawseum#founders" },
    { label: t("nav.contact"), href: "mailto:pawseum0@gmail.com" },
  ];

  const socialLinks = [
    {
      name: "Instagram",
      href: "https://www.instagram.com/pawseum_",
      icon: <Instagram className="w-5 h-5" />,
      color: "hover:text-pink-500",
    },
    {
      name: "Facebook",
      href: "https://www.facebook.com/people/Pawseum/61580631218892/",
      icon: <Facebook className="w-5 h-5" />,
      color: "hover:text-blue-600",
    },
    {
      name: "TikTok",
      href: "https://tiktok.com/@pawseum",
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
        </svg>
      ),
      color: "hover:text-black",
    },
    {
      name: "LinkedIn",
      href: "https://www.linkedin.com/in/guilherme-pinto-292493275/",
      icon: <Linkedin className="w-5 h-5" />,
      color: "hover:text-blue-700",
    },
  ];

  return (
    <footer
      className="bg-gradient-to-br from-gradient-purple via-middle-gradient to-gradient-blue text-harvest-gold"
      style={
        IS_NATIVE_APP
          ? { paddingBottom: "env(safe-area-inset-bottom)" }
          : undefined
      }
    >
      {" "}
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto  px-8  md:px-10 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 gap-x-20 text-snow/80">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-start space-x-3 mb-4">
              <img
                src="/assets/logos/logo.svg"
                alt={t("footer.logoAlt")}
                className="w-32 h-12"
              />
            </div>
            <p className="text-sm leading-relaxed mb-6 text-golden-beige/80">
              {t("footer.tagline")}
            </p>
            {/* Social Media */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-3 text-vanilla">
                {t("footer.followUs")}
              </h4>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <button
                    key={social.name}
                    onClick={() => openExternalUrl(social.href)}
                    className={`text-golden-beige/70 transition-all duration-300 transform hover:scale-110 ${social.color}`}
                    aria-label={social.name}
                  >
                    {social.icon}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-tabby-orange">
              {t("footer.quickNav")}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => navigate(link.href)}
                    className="flex items-center space-x-2 text-sm text-golden-beige/80 hover:text-tabby-orange transition-colors duration-300 group"
                  >
                    <span className="group-hover:text-tabby-orange transition-colors duration-300">
                      {link.icon}
                    </span>
                    <span>{link.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Adoption Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-tabby-orange">
              {t("footer.adoption")}
            </h3>
            <ul className="space-y-2">
              {adoptionLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => navigate(link.href)}
                    className="text-sm text-golden-beige/80 hover:text-tabby-orange transition-colors duration-300 text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* About & Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-tabby-orange">
              {t("footer.aboutContact")}
            </h3>
            <ul className="space-y-2 mb-6">
              {aboutLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => navigate(link.href)}
                    className="text-sm text-golden-beige/80 hover:text-tabby-orange transition-colors duration-300 text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 text-sm text-golden-beige/80">
                <Mail className="w-4 h-4 text-tabby-orange" />
                <a
                  href="mailto:pawseum0@gmail.com"
                  className="hover:text-tabby-orange transition-colors duration-300"
                >
                  pawseum0@gmail.com
                </a>
              </div>
              <div className="flex items-center space-x-2 text-sm text-golden-beige/80">
                <Phone className="w-4 h-4 text-tabby-orange" />
                <a
                  href="tel:+351123456789"
                  className="hover:text-tabby-orange transition-colors duration-300"
                >
                  +351 933144386
                </a>
              </div>
              <div className="flex items-start space-x-2 text-sm text-golden-beige/80">
                <MapPin className="w-4 h-4 text-tabby-orange mt-1 flex-shrink-0" />
                <span>Porto, Portugal</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-golden-beige/20">
        <div className="max-w-7xl mx-auto mr-24 px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row  items-center text-sm text-vanilla space-y-4 md:space-y-0">
            <div className="flex items-center justify-between space-x-14">
              <p>© {currentYear} Pawseum. {t("footer.rightsReserved")}</p>
              <div className="hidden md:flex items-center space-x-4">
                <button
                  onClick={() => navigate("/privacidade")}
                  className="hover:text-tabby-orange transition-colors duration-300"
                >
                  {t("nav.privacyPolicy")}
                </button>
                <button
                  onClick={() => navigate("/termos")}
                  className="hover:text-tabby-orange transition-colors duration-300"
                >
                  {t("nav.termsOfUse")}
                </button>
                <button
                  onClick={() => navigate("/cookies")}
                  className="hover:text-tabby-orange transition-colors duration-300"
                >
                  {t("footer.cookies")}
                </button>
              </div>

              <span className="flex items-center gap-4">
                <LanguageSwitcher />
                <span className="flex">
                  <Heart className="w-4 h-4 text-red-400 " />
                  <p className="pl-3">
                    {t("footer.loveMessage")}
                  </p>
                </span>
              </span>
            </div>
          </div>

          {/* Mobile Legal Links */}
          <div className="md:hidden flex flex-wrap justify-center items-center gap-4 mt-4 text-xs text-golden-beige/60">
            <button
              onClick={() => navigate("/privacidade")}
              className="hover:text-tabby-orange transition-colors duration-300"
            >
              {t("nav.privacyPolicy")}
            </button>
            <button
              onClick={() => navigate("/termos")}
              className="hover:text-tabby-orange transition-colors duration-300"
            >
              {t("nav.termsOfUse")}
            </button>
            <button
              onClick={() => navigate("/cookies")}
              className="hover:text-tabby-orange transition-colors duration-300"
            >
              {t("footer.cookies")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
